const configUrl = '/pwa-config.json';
const versionUrl = '/version.json';
// Fired when an installed worker is waiting for the user to approve activation.
export const serviceWorkerUpdateEvent = 'eliteedge:sw-update';
// Fired after registration, disablement, or failure for diagnostics/telemetry.
export const serviceWorkerStatusEvent = 'eliteedge:sw-status';

async function fetchJson(url) {
  const response = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

async function unregisterEliteEdgeWorkers() {
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter(name => name.startsWith('eliteedge-beta-shell-'))
        .map(name => caches.delete(name))
    );
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations
      .filter(registration => registration.scope.startsWith(window.location.origin))
      .map(registration => registration.unregister())
  );
}

function reportStatus(status, reason) {
  window.dispatchEvent(new CustomEvent(serviceWorkerStatusEvent, {
    detail: { status, reason: reason || null },
  }));
  window.dispatchEvent(new CustomEvent('eliteedge:analytics', {
    detail: {
      event: 'pwa_service_worker_status',
      status,
      reason: reason || undefined,
    },
  }));
}

export async function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return null;

  try {
    let control;
    try {
      control = await fetchJson(configUrl);
    } catch (error) {
      await unregisterEliteEdgeWorkers();
      reportStatus('failed', 'control_unavailable');
      return null;
    }

    const minimumBuild = Number(control?.minimumServiceWorkerBuild);
    if (
      typeof control?.enabled !== 'boolean' ||
      !Number.isInteger(minimumBuild) ||
      minimumBuild < 1
    ) {
      await unregisterEliteEdgeWorkers();
      reportStatus('failed', 'invalid_control');
      return null;
    }
    if (control.enabled === false) {
      await unregisterEliteEdgeWorkers();
      reportStatus('disabled', 'kill_switch');
      return null;
    }

    let version;
    try {
      version = await fetchJson(versionUrl);
    } catch (error) {
      await unregisterEliteEdgeWorkers();
      reportStatus('failed', 'version_unavailable');
      return null;
    }

    const build = Number(version?.build);
    if (!Number.isInteger(build) || build < 1) {
      await unregisterEliteEdgeWorkers();
      reportStatus('failed', 'invalid_build');
      return null;
    }
    if (build < minimumBuild) {
      await unregisterEliteEdgeWorkers();
      reportStatus('disabled', 'below_minimum_build');
      return null;
    }

    const registration = await navigator.serviceWorker.register(
      `/service-worker.js?build=${encodeURIComponent(build)}`,
      { updateViaCache: 'none' }
    );

    if (registration.waiting) {
      window.dispatchEvent(new CustomEvent(serviceWorkerUpdateEvent));
    }
    registration.addEventListener('updatefound', () => {
      const worker = registration.installing;
      if (!worker) return;
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          window.dispatchEvent(new CustomEvent(serviceWorkerUpdateEvent));
        }
      });
    });
    reportStatus('registered');
    return registration;
  } catch (error) {
    console.warn('[PWA] Service worker registration skipped:', error.message);
    reportStatus('failed', 'registration_error');
    return null;
  }
}

/**
 * Requests activation of a waiting worker. A true result means the request was
 * sent; completion is reported separately through navigator.serviceWorker's
 * controllerchange event.
 */
export async function requestWaitingServiceWorkerActivation() {
  if (!('serviceWorker' in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.waiting) return false;
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}
